/**
 * First-party capture of Google Ads click identifiers (gclid / gbraid / wbraid).
 *
 * The click id is captured on landing by a tiny inline script in the root
 * layout (parses the URL query, writes this cookie) and read back here at
 * booking/confirm time so it can be sent to the API and stored on the booking
 * row. That lets the cancellation-retraction cron target ONLY bookings Google
 * recorded as a conversion — a booking carrying a click id came from a Google
 * ad, one that carries none never did.
 *
 * Cookie shape: URL-encoded JSON of whichever ids are present, e.g.
 *   sukuto_gclid = %7B%22gclid%22%3A%22Cj0...%22%7D
 * Lifetime 30 days — the click-through lookback window configured on the
 * "Booking Success" conversion action (verified via the Ads API), NOT the 90
 * days of Google's own `_gcl_aw` cookie. Beyond 30 days Google credits no
 * conversion to the click, so a longer-lived id would only feed the retraction
 * cron bookings whose conversion never existed ("CONVERSION_NOT_FOUND").
 * Re-check this if the conversion action's window is ever changed.
 * Last click wins: a fresh click id overwrites the cookie (last-click parity
 * with Google Ads attribution). The cookie is only written when a click id is
 * actually present, so ordinary navigation never clobbers a stored id.
 */

export const GADS_COOKIE = "sukuto_gclid";
export const GADS_CLICK_ID_KEYS = ["gclid", "gbraid", "wbraid"];

/**
 * Read the stored Google click ids. Returns an object with only the keys that
 * are present (e.g. { gclid: "..." }), or null when none are stored. Safe to
 * call during SSR — returns null when there is no document.
 *
 * @returns {{gclid?: string, gbraid?: string, wbraid?: string}|null}
 */
export function readGoogleClickIds() {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|;\s*)sukuto_gclid=([^;]*)/);
  if (!match) return null;

  let raw = match[1];
  try {
    raw = decodeURIComponent(raw);
  } catch {
    // Value was not URL-encoded; use it as-is.
  }

  try {
    const obj = JSON.parse(raw);
    const out = {};
    for (const key of GADS_CLICK_ID_KEYS) {
      if (obj && typeof obj[key] === "string" && obj[key]) {
        out[key] = obj[key];
      }
    }
    return Object.keys(out).length ? out : null;
  } catch {
    return null;
  }
}
